import { Component, Element, Event, EventEmitter, h, Prop, State, Watch, Host } from '@stencil/core';
import { E2ESupportForum } from './e2e-support-forum.interface';
import { fetchWithTimeout } from '@ticom/web-component-utils';

@Component({
    tag: 'ti-e2e-support-forums',
    styleUrl: 'e2e-support-forums.scss',
    shadow: true
})

export class E2eSupportForums {

    private _io: IntersectionObserver;
    private readonly _statusType = {
        LOADING: 'LOADING',
        READY: 'READY',
        ERROR: 'ERROR'
    }

    @Element() hostElement: HTMLTiE2eSupportForumsElement;

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @default _statusType.Loading
     */
    @State() status: string = this._statusType.LOADING;

    /**
     * State entry for keeping track of our item data
     */
    @State() items: E2ESupportForum[] = [];
    @Watch('items')
    handleItemsUpdated() {
        this.tiItemsUpdated.emit({
            items: this.items
        });
    }

    /**
     * Api URL - where we get the data from
     * @type {string}
     */
    @Prop() apiUrl: string = '';
    @Watch('apiUrl')
    handleApiUrlChange(newValue: string, oldValue: string) {
        this._dataPropChange(newValue, oldValue);
    }

    /**
     * Data Lid for metrics tracking.
     * @type {string}
     */
    @Prop() dataLid: string = 'folder_product_rtp_recommendations_jack_e2e_product_recommendation_en';

    /**
     * if lazy loaded or not
     */
    @Prop() lazy: boolean = false;

    /**
     * Number of forum threads to display.
     *
     * @type {number}
     * @default 6
     */
    @Prop() numToDisplay: number = 6;

    /**
     * Event for when items are updated
     */
    @Event() tiItemsUpdated: EventEmitter<{ items: E2ESupportForum[] }>;
    /**
     * Event for when there's a problem getting the data
     */
    @Event() tiDataError: EventEmitter<{ error: { message: string } }>;

    /**
     * Lifecycle event handler
     */
    componentWillLoad() {
        if (!this.lazy) {
            this._updateData();
        }
    }

    componentDidLoad() {
        this._addIntersectionObserver();
    }

    disconnectedCallback() {
        this._removeIntersectionObserver();
    }

    private _addIntersectionObserver() {
        if (this.lazy && 'IntersectionObserver' in window) {
            this._io = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    this._updateData();
                    this._removeIntersectionObserver();
                }
            });
            if (this._io['USE_MUTATION_OBSERVER'] != null) {
                this._io['USE_MUTATION_OBSERVER'] = false;
            }
            this._io.observe(this.hostElement);
        }
    }

    private _removeIntersectionObserver() {
        if (this._io) {
            this._io.disconnect();
            this._io = null;
        }
    }

    private _htmlDecode(input: string) {
        if (input) {
            const doc = new DOMParser().parseFromString(input, 'text/html');
            return doc.documentElement.textContent;
        }
        return null;
    }

    private async _updateData() {
        let json: E2ESupportForum[] | { error: unknown };
        // First try to get data from preloaded script tag if exists
        const preloadScriptEl = this.hostElement.querySelector<HTMLScriptElement>('script[type=\'application/json\']');
        if (preloadScriptEl?.textContent?.trim()) {
            json = JSON.parse(preloadScriptEl.textContent.trim());
        } else {
            // Make API call if preloaded data not available
            try {
                const response = await fetchWithTimeout(`${this.apiUrl}`);
                if (response.ok) {
                    json = await response.json();
                }
            } catch (err) {
                this.status = this._statusType.ERROR;
                this.tiDataError.emit({
                    error: err
                });
            }
        }
        if (json) {
            // if the item isn't found, the web service is actually set
            // up to return a 200, but with a "error" property in the
            // json response. This helps avoid false alarms with monitoring,
            // as there are probably a number of parts that don't have data
            // but that doesn't mean we have a real problem.
            if ('error' in json) {
                throw new Error(`Request for ${this.apiUrl} returned 404`);
            }
            this.items = json.slice();
            if (this.items.length > 0) {
                this.items.forEach(item => {
                    item.description = this._htmlDecode(item.description);
                });
                this.status = this._statusType.READY;
            } else {
                this.status = this._statusType.ERROR;
            }
        } else {
            this.status = this._statusType.ERROR;
            this.tiDataError.emit({
                error: {
                    message: 'No JSON data'
                }
            });
        }
    }

    private _dataPropChange(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue.length > 0) {
            this.status = this._statusType.LOADING;
        }
        this._updateData();
    }

    render() {
        if (this.status == this._statusType.LOADING) {
            return (<div class="ti-e2e-support-forums-loader-container"><ti-progress /></div>);
        } else if (this.status == this._statusType.READY) {
            const numToDisplay = Math.min(this.numToDisplay, this.items.length);
            return (
                <Host>
                    <ul class={{
                        'ti-e2e-support-forums-container': true,
                        'ti-e2e-support-forums-single-column': this.items.length < 2
                    }}>
                        {
                            this.items.slice(0, numToDisplay).map(item => {
                                const metricsId = item.faqFlag
                                    ? 'e2e_support_faq'
                                    : 'e2e_support_nonfaq';
                                return (
                                    <li class="ti-e2e-support-forums-row-item">
                                        <h4><a data-di-id={metricsId} slot="title" href={item.url}>{item.description}</a></h4>
                                        <p slot="description">{`${item.preview}...`}</p>
                                    </li>
                                );
                            })
                        }
                    </ul>
                </Host>
            );
        } else {
            return (<div></div>);
        }
    }
}
